import React, { useEffect, useState, useCallback } from 'react';
import { Search, Plus, ShoppingCart, FileText, DollarSign } from 'lucide-react';
import { salesApi } from '../api/sales';
import { contactsApi } from '../api/contacts';
import { productsApi } from '../api/products';
import { purchaseApi } from '../api/purchase';
import { coaApi } from '../api/coa';
import { useToast } from '../hooks/useToast';

interface Sale { id:number; sale_no:string; customer_name:string; total_amount:number; gross_profit:number; status:string; status_color:string; sale_type:string; created_at:string; due_date:string; }
const colorMap: Record<string,string> = { blue:'badge-blue', green:'badge-green', amber:'badge-amber', gray:'badge-gray', red:'badge-red' };

const Sales: React.FC = () => {
  const { showToast } = useToast();
  const [sales, setSales]         = useState<Sale[]>([]);
  const [total, setTotal]         = useState(0);
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(true);
  const [mode, setMode]           = useState<'list'|'quick'|'invoice'|'payment'>('list');
  const [saving, setSaving]       = useState(false);
  const [contacts, setContacts]   = useState<any[]>([]);
  const [products, setProducts]   = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [branches, setBranches]   = useState<any[]>([]);
  const [cashAccounts, setCashAccounts] = useState<any[]>([]);
  const [form, setForm]   = useState({ contact_id:'', branch_id:'', cash_account_id:'', due_date:'', notes:'' });
  const [payForm, setPayForm] = useState({ contact_id:'', sale_id:'', account_id:'', amount:'', branch_id:'', payment_ref:'', notes:'' });
  const [lines, setLines] = useState([{ product_id:'', location_id:'', qty:'1', unit_price:'', discount:'' }]);

  const loadMeta = () => {
    contactsApi.list().then(r => setContacts(r.data.data));
    productsApi.list().then(r => setProducts(r.data.data));
    purchaseApi.locations().then(r => setLocations(r.data.data));
    purchaseApi.branches().then(r => setBranches(r.data.data));
    coaApi.list().then(r => setCashAccounts(r.data.data.filter((a: any) => a.type_name === 'Asset' && a.subtype_name === 'Current Asset')));
  };

  const load = useCallback(() => {
    setLoading(true);
    salesApi.list({ search }).then(res => { setSales(res.data.data); setTotal(res.data.total); setLoading(false); });
  }, [search]);

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const addLine = () => setLines(l => [...l, { product_id:'', location_id:'', qty:'1', unit_price:'', discount:'' }]);
  const removeLine = (i: number) => setLines(l => l.filter((_,idx) => idx !== i));
  const updateLine = (i: number, field: string, val: string) => {
    setLines(l => l.map((line, idx) => {
      if (idx !== i) return line;
      const updated = { ...line, [field]: val };
      if (field === 'product_id' && val) {
        const p = products.find((p: any) => p.id === Number(val));
        if (p) updated.unit_price = String(p.selling_price);
      }
      return updated;
    }));
  };

  const grandTotal = lines.reduce((s,l) => s + (Number(l.qty)*Number(l.unit_price)-Number(l.discount)), 0);

  const handleQuickSale = async () => {
    if (!form.branch_id || !form.cash_account_id) { showToast('Branch and cash account required', 'error'); return; }
    if (lines.some(l => !l.product_id || !l.location_id)) { showToast('Fill all line items', 'error'); return; }
    setSaving(true);
    try {
      const items = lines.filter(l => l.product_id && l.location_id).map(l => ({ product_id:Number(l.product_id), location_id:Number(l.location_id), qty:Number(l.qty), unit_price:Number(l.unit_price), discount:Number(l.discount) }));
      const res = await salesApi.quickSale({ ...form, contact_id:form.contact_id?Number(form.contact_id):null, branch_id:Number(form.branch_id), cash_account_id:Number(form.cash_account_id), items });
      showToast(`${res.data.data.sale_no} posted! Total: ${Number(res.data.data.total).toFixed(2)}`);
      setMode('list'); setLines([{ product_id:'', location_id:'', qty:'1', unit_price:'0', discount:'0' }]); load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleInvoiceSale = async () => {
    if (!form.contact_id || !form.branch_id) { showToast('Customer and branch required', 'error'); return; }
    if (lines.some(l => !l.product_id || !l.location_id)) { showToast('Fill all line items', 'error'); return; }
    setSaving(true);
    try {
      const items = lines.filter(l => l.product_id && l.location_id).map(l => ({ product_id:Number(l.product_id), location_id:Number(l.location_id), qty:Number(l.qty), unit_price:Number(l.unit_price), discount:Number(l.discount) }));
      const res = await salesApi.invoiceSale({ ...form, contact_id:Number(form.contact_id), branch_id:Number(form.branch_id), items });
      showToast(`${res.data.data.sale_no} posted!`);
      setMode('list'); setLines([{ product_id:'', location_id:'', qty:'1', unit_price:'0', discount:'0' }]); load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handlePayment = async () => {
    if (!payForm.contact_id || !payForm.account_id || !payForm.amount) { showToast('Customer, account and amount required', 'error'); return; }
    setSaving(true);
    try {
      const res = await salesApi.receivePayment({ ...payForm, contact_id:Number(payForm.contact_id), sale_id:payForm.sale_id?Number(payForm.sale_id):null, account_id:Number(payForm.account_id), amount:Number(payForm.amount), branch_id:Number(payForm.branch_id) });
      showToast(res.data.message); setMode('list'); load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };



  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Sales</h1>
        <div style={{ display:'flex', gap:8 }}>
          <button className={`btn ${mode==='quick' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setMode(mode==='quick'?'list':'quick')}><ShoppingCart size={13} /> Quick Sale</button>
          <button className={`btn ${mode==='invoice' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setMode(mode==='invoice'?'list':'invoice')}><FileText size={13} /> Invoice</button>
          <button className={`btn ${mode==='payment' ? 'btn-success' : 'btn-secondary'}`} onClick={() => setMode(mode==='payment'?'list':'payment')}><DollarSign size={13} /> Receive Payment</button>
        </div>
      </div>

      {mode === 'quick' && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header"><span>Quick Sale — Cash</span><button className="btn btn-secondary btn-sm" onClick={() => setMode('list')}>Cancel</button></div>
          <div className="card-body">
            <div className="form-grid-4" style={{ marginBottom:14 }}>
              <div className="form-group"><label className="form-label">Customer</label>
                <select className="form-control" value={form.contact_id} onChange={e => setForm(f=>({...f,contact_id:e.target.value}))}>
                  <option value="">Walk-in / Cash</option>
                  {contacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Branch *</label>
                <select className="form-control" value={form.branch_id} onChange={e => setForm(f=>({...f,branch_id:e.target.value}))}>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Cash Account *</label>
                <select className="form-control" value={form.cash_account_id} onChange={e => setForm(f=>({...f,cash_account_id:e.target.value}))}>
                  <option value="">Select account</option>
                  {cashAccounts.map(a => <option key={a.id} value={a.id}>{a.account_code} — {a.account_name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Notes</label>
                <input className="form-control" value={form.notes} onChange={e => setForm(f=>({...f,notes:e.target.value}))} />
              </div>
            </div>
            <>
      <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:8 }}>
        <thead><tr style={{ background:'var(--sur2)' }}>
          <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Product</th>
          <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Location</th>
          <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Qty</th>
          <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Price</th>
          <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Disc</th>
          <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Total</th>
          <th></th>
        </tr></thead>
        <tbody>
          {lines.map((line, i) => (
            <tr key={i} style={{ borderBottom:'1px solid var(--bdr)' }}>
              <td style={{ padding:'6px 4px' }}>
                <select className="form-control" value={line.product_id} onChange={e => updateLine(i,'product_id',e.target.value)} style={{ fontSize:12 }}>
                  <option value="">Select product</option>
                  {products.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </td>
              <td style={{ padding:'6px 4px' }}>
                <select className="form-control" value={line.location_id} onChange={e => updateLine(i,'location_id',e.target.value)} style={{ fontSize:12 }}>
                  <option value="">Select location</option>
                  {locations.map((l: any) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </td>
              <td style={{ padding:'6px 4px', width:80 }}><input className="form-control mono" type="text" inputMode="decimal" value={line.qty} onChange={e => updateLine(i,'qty',e.target.value)} style={{ textAlign:'right' }} /></td>
              <td style={{ padding:'6px 4px', width:100 }}><input className="form-control mono" type="text" inputMode="decimal" value={line.unit_price} onChange={e => updateLine(i,'unit_price',e.target.value)} style={{ textAlign:'right' }} /></td>
              <td style={{ padding:'6px 4px', width:80 }}><input className="form-control mono" type="text" inputMode="decimal" value={line.discount} onChange={e => updateLine(i,'discount',e.target.value)} style={{ textAlign:'right' }} /></td>
              <td style={{ padding:'6px 10px', textAlign:'right', fontFamily:'monospace', fontWeight:600, fontSize:12 }}>{(Number(line.qty)*Number(line.unit_price)-Number(line.discount)).toFixed(2)}</td>
              <td><button onClick={() => removeLine(i)} style={{ background:'none', border:'none', color:'var(--red)', cursor:'pointer', fontSize:18, padding:'0 4px' }}>×</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <button className="btn btn-secondary btn-sm" onClick={addLine}><Plus size={12} /> Add Line</button>
        <div style={{ fontFamily:'monospace', fontWeight:700, fontSize:16, color:'var(--green)' }}>Total: {grandTotal.toFixed(2)}</div>
      </div>
    </>
            <div style={{ marginTop:14 }}>
              <button className="btn btn-success" onClick={handleQuickSale} disabled={saving}>{saving ? 'Posting...' : 'Post Cash Sale'}</button>
            </div>
          </div>
        </div>
      )}

      {mode === 'invoice' && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header"><span>Invoice Sale — Credit</span><button className="btn btn-secondary btn-sm" onClick={() => setMode('list')}>Cancel</button></div>
          <div className="card-body">
            <div className="form-grid-4" style={{ marginBottom:14 }}>
              <div className="form-group"><label className="form-label">Customer *</label>
                <select className="form-control" value={form.contact_id} onChange={e => setForm(f=>({...f,contact_id:e.target.value}))}>
                  <option value="">Select customer</option>
                  {contacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Branch *</label>
                <select className="form-control" value={form.branch_id} onChange={e => setForm(f=>({...f,branch_id:e.target.value}))}>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Due Date</label>
                <input className="form-control" type="date" value={form.due_date} onChange={e => setForm(f=>({...f,due_date:e.target.value}))} />
              </div>
              <div className="form-group"><label className="form-label">Notes</label>
                <input className="form-control" value={form.notes} onChange={e => setForm(f=>({...f,notes:e.target.value}))} />
              </div>
            </div>
            <>
      <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:8 }}>
        <thead><tr style={{ background:'var(--sur2)' }}>
          <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Product</th>
          <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Location</th>
          <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Qty</th>
          <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Price</th>
          <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Disc</th>
          <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Total</th>
          <th></th>
        </tr></thead>
        <tbody>
          {lines.map((line, i) => (
            <tr key={i} style={{ borderBottom:'1px solid var(--bdr)' }}>
              <td style={{ padding:'6px 4px' }}>
                <select className="form-control" value={line.product_id} onChange={e => updateLine(i,'product_id',e.target.value)} style={{ fontSize:12 }}>
                  <option value="">Select product</option>
                  {products.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </td>
              <td style={{ padding:'6px 4px' }}>
                <select className="form-control" value={line.location_id} onChange={e => updateLine(i,'location_id',e.target.value)} style={{ fontSize:12 }}>
                  <option value="">Select location</option>
                  {locations.map((l: any) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </td>
              <td style={{ padding:'6px 4px', width:80 }}><input className="form-control mono" type="text" inputMode="decimal" value={line.qty} onChange={e => updateLine(i,'qty',e.target.value)} style={{ textAlign:'right' }} /></td>
              <td style={{ padding:'6px 4px', width:100 }}><input className="form-control mono" type="text" inputMode="decimal" value={line.unit_price} onChange={e => updateLine(i,'unit_price',e.target.value)} style={{ textAlign:'right' }} /></td>
              <td style={{ padding:'6px 4px', width:80 }}><input className="form-control mono" type="text" inputMode="decimal" value={line.discount} onChange={e => updateLine(i,'discount',e.target.value)} style={{ textAlign:'right' }} /></td>
              <td style={{ padding:'6px 10px', textAlign:'right', fontFamily:'monospace', fontWeight:600, fontSize:12 }}>{(Number(line.qty)*Number(line.unit_price)-Number(line.discount)).toFixed(2)}</td>
              <td><button onClick={() => removeLine(i)} style={{ background:'none', border:'none', color:'var(--red)', cursor:'pointer', fontSize:18, padding:'0 4px' }}>×</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <button className="btn btn-secondary btn-sm" onClick={addLine}><Plus size={12} /> Add Line</button>
        <div style={{ fontFamily:'monospace', fontWeight:700, fontSize:16, color:'var(--green)' }}>Total: {grandTotal.toFixed(2)}</div>
      </div>
    </>
            <div style={{ marginTop:14 }}>
              <button className="btn btn-primary" onClick={handleInvoiceSale} disabled={saving}>{saving ? 'Posting...' : 'Post Invoice'}</button>
            </div>
          </div>
        </div>
      )}

      {mode === 'payment' && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header"><span>Receive Customer Payment</span><button className="btn btn-secondary btn-sm" onClick={() => setMode('list')}>Cancel</button></div>
          <div className="card-body">
            <div className="form-grid-3" style={{ marginBottom:14 }}>
              <div className="form-group"><label className="form-label">Customer *</label>
                <select className="form-control" value={payForm.contact_id} onChange={e => setPayForm(f=>({...f,contact_id:e.target.value}))}>
                  <option value="">Select customer</option>
                  {contacts.map(c => <option key={c.id} value={c.id}>{c.name} (AR: {Number(c.ar_balance||0).toFixed(2)})</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Invoice (optional)</label>
                <select className="form-control" value={payForm.sale_id} onChange={e => setPayForm(f=>({...f,sale_id:e.target.value}))}>
                  <option value="">General payment</option>
                  {sales.filter(s => s.customer_name === contacts.find((c:any) => c.id === Number(payForm.contact_id))?.name).map(s => (
                    <option key={s.id} value={s.id}>{s.sale_no} — {Number(s.total_amount).toFixed(2)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Deposit Account *</label>
                <select className="form-control" value={payForm.account_id} onChange={e => setPayForm(f=>({...f,account_id:e.target.value}))}>
                  <option value="">Select account</option>
                  {cashAccounts.map(a => <option key={a.id} value={a.id}>{a.account_code} — {a.account_name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-grid-3">
              <div className="form-group"><label className="form-label">Amount *</label>
                <input className="form-control mono" type="number" value={payForm.amount} onChange={e => setPayForm(f=>({...f,amount:e.target.value}))} placeholder="0.00" />
              </div>
              <div className="form-group"><label className="form-label">Reference</label>
                <input className="form-control" value={payForm.payment_ref} onChange={e => setPayForm(f=>({...f,payment_ref:e.target.value}))} placeholder="Cheque no, transfer ref..." />
              </div>
              <div className="form-group"><label className="form-label">Branch *</label>
                <select className="form-control" value={payForm.branch_id} onChange={e => setPayForm(f=>({...f,branch_id:e.target.value}))}>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            </div>
            <button className="btn btn-success" onClick={handlePayment} disabled={saving}>{saving ? 'Posting...' : 'Post Payment'}</button>
          </div>
        </div>
      )}

      <div className="filter-bar">
        <div className="search-box"><Search size={13} color="var(--tx3)" /><input placeholder="Search sales..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        <span className="record-count">{total} sales</span>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr>
            <th>Sale No</th><th>Customer</th><th>Type</th>
            <th style={{textAlign:'right'}}>Total</th>
            <th style={{textAlign:'right'}}>Gross Profit</th>
            <th>Due Date</th><th>Status</th><th>Date</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{textAlign:'center',padding:32,color:'var(--tx3)'}}>Loading...</td></tr>
            ) : sales.length === 0 ? (
              <tr><td colSpan={8}><div className="empty-state"><p>No sales yet.</p></div></td></tr>
            ) : sales.map(s => (
              <tr key={s.id}>
                <td className="td-code">{s.sale_no}</td>
                <td style={{fontWeight:600}}>{s.customer_name || 'Walk-in'}</td>
                <td><span className="badge badge-blue">{s.sale_type}</span></td>
                <td className="td-num td-green">{Number(s.total_amount).toFixed(2)}</td>
                <td className="td-num" style={{color:Number(s.gross_profit)>=0?'var(--green)':'var(--red)'}}>{Number(s.gross_profit).toFixed(2)}</td>
                <td style={{color:'var(--tx3)',fontSize:11}}>{s.due_date ? new Date(s.due_date).toLocaleDateString() : '—'}</td>
                <td><span className={`badge ${colorMap[s.status_color]||'badge-gray'}`}>{s.status}</span></td>
                <td style={{color:'var(--tx3)',fontSize:11}}>{new Date(s.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default Sales;
